import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('selectedTheme');
    return savedTheme || 'ocean';
  });

  const [modules, setModules] = useState({
    incomeExpenses: {
      enabled: true,
      data: [
        { label: 'Jan', income: 4250, expenses: 2450 },
        { label: 'Feb', income: 4250, expenses: 2300 },
        { label: 'Mar', income: 4500, expenses: 2600 },
        { label: 'Apr', income: 4250, expenses: 2200 },
        { label: 'May', income: 5000, expenses: 2800 },
        { label: 'Jun', income: 4250, expenses: 2500 },
      ]
    },
    categories: {
      enabled: true,
      data: [
        { label: 'Housing', value: 1200, color: '#3b82f6' },
        { label: 'Food', value: 600, color: '#10b981' },
        { label: 'Transportation', value: 400, color: '#f59e0b' },
        { label: 'Utilities', value: 300, color: '#ef4444' },
        { label: 'Entertainment', value: 200, color: '#8b5cf6' },
        { label: 'Health', value: 150, color: '#ec4899' },
        { label: 'Other', value: 580, color: '#6b7280' },
      ]
    },
    networth: {
      enabled: true,
      data: [
        { label: 'Jan', value: 45000 },
        { label: 'Feb', value: 46200 },
        { label: 'Mar', value: 47800 },
        { label: 'Apr', value: 48500 },
        { label: 'May', value: 51200 },
        { label: 'Jun', value: 53400 },
      ]
    },
    monthly: {
      enabled: true,
      data: [
        { label: 'Jan', income: 4250, expenses: 2450, savings: 1800 },
        { label: 'Feb', income: 4250, expenses: 2300, savings: 1950 },
        { label: 'Mar', income: 4500, expenses: 2600, savings: 1900 },
        { label: 'Apr', income: 4250, expenses: 2200, savings: 2050 },
        { label: 'May', income: 5000, expenses: 2800, savings: 2200 },
        { label: 'Jun', income: 4250, expenses: 2500, savings: 1750 },
      ]
    },
    charts: {
      enabled: true,
      data: [
        { label: 'Q1', value: 65, color: '#0ea5e9' },
        { label: 'Q2', value: 78, color: '#3b82f6' },
        { label: 'Q3', value: 92, color: '#6366f1' },
        { label: 'Q4', value: 45, color: '#8b5cf6' }
      ]
    },
    analytics: {
      enabled: true,
      data: [
        { label: 'Users', value: 2847, trend: 'up', trendValue: '12%' },
        { label: 'Revenue', value: 125000, trend: 'up', trendValue: '8%' },
        { label: 'Engagement', value: 67.3, trend: 'down', trendValue: '3%' }
      ]
    }
  });

  const themes = {
    ocean: {
      textPrimary: '#0c4a6e',
      textSecondary: '#64748b',
      textTertiary: '#94a3b8',
      bgPrimary: '#ffffff',
      bgSecondary: '#f0f9ff',
      bgTertiary: '#e0f2fe',
      accent: '#0ea5e9',
      accentContrast: '#ffffff',
      border: '#bae6fd'
    },
    sunset: {
      textPrimary: '#451a03',
      textSecondary: '#78350f',
      textTertiary: '#92400e',
      bgPrimary: '#ffffff',
      bgSecondary: '#fef2f2',
      bgTertiary: '#fee2e2',
      accent: '#d97706',
      accentContrast: '#ffffff',
      border: '#fcd34d'
    },
    forest: {
      textPrimary: '#20322d',
      textSecondary: '#3f621d',
      textTertiary: '#65a30d',
      bgPrimary: '#ffffff',
      bgSecondary: '#f0fdf4',
      bgTertiary: '#dcfce7',
      accent: '#406036',
      accentContrast: '#ffffff',
      border: '#bbf7d0'
    },
    // forestDark: {
    //   textPrimary: '#ffffff',
    //   textSecondary: '#f0fdf4',
    //   textTertiary: '#dcfce7',
    //   bgPrimary: '#20322d',
    //   bgSecondary: '#3f621d',
    //   bgTertiary: '#65a30d',
    //   accent: '#406036',
    //   accentContrast: '#ffffff',
    //   border: '#bbf7d0'
    // },
    midnight: {
      textPrimary: '#f8fafc',
      textSecondary: '#cbd5e1',
      textTertiary: '#94a3b8',
      bgPrimary: '#0f172a',
      bgSecondary: '#1e293b',
      bgTertiary: '#334155',
      accent: '#9084eb',
      accentContrast: '#0f172a',
      border: '#475569'
    }
  };

  useEffect(() => {
    // Apply CSS custom properties
    const root = document.documentElement;
    const currentTheme = themes[theme];
    
    // Primary/secondary/tertiary colors (for text and charts)
    root.style.setProperty('--theme-primary', currentTheme.textPrimary);
    root.style.setProperty('--theme-secondary', currentTheme.textSecondary);
    root.style.setProperty('--theme-tertiary', currentTheme.textTertiary);
    
    // Background colors
    root.style.setProperty('--theme-bg-primary', currentTheme.bgPrimary);
    root.style.setProperty('--theme-bg-secondary', currentTheme.bgSecondary);
    root.style.setProperty('--theme-bg-tertiary', currentTheme.bgTertiary);
    
    // Accent and border
    root.style.setProperty('--theme-accent', currentTheme.accent);
    root.style.setProperty('--theme-accent-contrast', currentTheme.accentContrast);
    root.style.setProperty('--theme-border', currentTheme.border);
    
    // Save to localStorage
    localStorage.setItem('selectedTheme', theme);
    localStorage.setItem('enabledModules', JSON.stringify(modules));
  }, [theme, modules]);

  const updateTheme = (newTheme) => {
    setTheme(newTheme);
  };

  const updateModules = (moduleName, config) => {
    setModules(prev => ({
      ...prev,
      [moduleName]: { ...prev[moduleName], ...config }
    }));
  };

  const getModuleData = (moduleName, index) => {
    const module = modules[moduleName];
    if (module && module.enabled && module.data && module.data[index] !== undefined) {
      return module.data[index];
    }
    return null;
  };

  const value = {
    theme,
    themes,
    modules,
    updateTheme,
    updateModules,
    getModuleData
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
